import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { PomodoroProvider } from './contexts/PomodoroContext';

import AppLayout          from './components/AppLayout';
import HomePage           from './pages/Home';
import LoginPage          from './pages/Login';
import RegisterPage       from './pages/Register';
import ForgotPasswordPage from './pages/ForgotPassword';
import TasksPage          from './pages/Tasks';
import AnalyticsPage      from './pages/Analytics';
import CategoriesPage     from './pages/Categories';
import SettingsPage       from './pages/Settings';
import HabitsPage         from './pages/Habits';
import { Spinner }        from './components/ui';
import VerifyEmailPage from './pages/VerifyEmail';

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullPageSpinner />;
  return user ? children : <Navigate to="/login" replace />;
}

function GuestOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user ? <Navigate to="/app" replace /> : children;
}

function MaybeAuth({ children }) {
  const { loading } = useAuth();
  if (loading) return <FullPageSpinner />;
  return children;
}

function FullPageSpinner() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'var(--bg)' }}>
      <Spinner size={32} />
    </div>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <PomodoroProvider>
        <BrowserRouter>
          <style>{`
            *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

            :root {
              /* Brand — overridden by ThemeContext on mount */
              --brand-grad:   linear-gradient(135deg, #4A90E2 0%, #36C9BE 50%, #3EE07A 100%);
              --brand-shadow: rgba(54,201,190,0.25);
              --accent:       #36C9BE;
              --accent-a:     #4A90E2;
              --accent-b:     #36C9BE;
              --accent-c:     #3EE07A;
              --accent-ring:  rgba(54,201,190,0.18);
              --accent-light: rgba(54,201,190,0.10);

              /* Light theme defaults */
              --bg:         #f7f7f5;
              --surface:    #ffffff;
              --surface2:   #fafaf8;
              --input-bg:   #fafaf8;
              --border:     #e8e8e4;
              --hover:      #f2f2ef;
              --divider:    #f0f0ec;
              --text:       #1a1a1a;
              --text2:      #666666;
              --text3:      #aaaaaa;
              --sidebar-bg: #fafaf8;
            }

            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
              background: var(--bg);
              color: var(--text);
              -webkit-font-smoothing: antialiased;
            }

            input, select, textarea, button { font-family: inherit; }

            ::-webkit-scrollbar { width: 5px; height: 5px; }
            ::-webkit-scrollbar-track { background: transparent; }
            ::-webkit-scrollbar-thumb { background: var(--border); border-radius: 10px; }
            ::-webkit-scrollbar-thumb:hover { background: var(--text3); }

            @keyframes modalIn {
              from { opacity: 0; transform: translateY(8px) scale(0.97); }
              to   { opacity: 1; transform: translateY(0) scale(1); }
            }
          `}</style>

          <Routes>
            {/* Landing page */}
            <Route path="/" element={<HomePage />} />

            {/* Auth pages */}
            <Route path="/login"           element={<GuestOnly><LoginPage /></GuestOnly>} />
            <Route path="/register"        element={<GuestOnly><RegisterPage /></GuestOnly>} />
            <Route path="/verify-email"  element={<GuestOnly><VerifyEmailPage /></GuestOnly>} />
            <Route path="/forgot-password" element={<GuestOnly><ForgotPasswordPage /></GuestOnly>} />

            {/* App — tasks open to guests, other pages require auth */}
            <Route path="/app" element={<MaybeAuth><AppLayout /></MaybeAuth>}>
              <Route index             element={<TasksPage />} />
              <Route path="analytics"  element={<RequireAuth><AnalyticsPage /></RequireAuth>} />
              <Route path="categories" element={<RequireAuth><CategoriesPage /></RequireAuth>} />
              <Route path="habits"     element={<MaybeAuth><HabitsPage /></MaybeAuth>} />
              <Route path="settings"   element={<RequireAuth><SettingsPage /></RequireAuth>} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        </PomodoroProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}